import {gql} from '@apollo/client';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import NoFlowchartViewer from 'processes/graph-builder-processes/flowchart-viz/NoFlowchartViewer';
import {isRouterNodeDetailsGQL} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForGQL';
import {isTerminalStatus} from 'processes/status/TaskStatusUtil';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  ProcessFlowchartProcessQuery,
  ProcessFlowchartProcessQueryVariables,
} from './__generated/ProcessFlowchartProcessQuery';
import {ProcessFlowchartStructureQuery} from './__generated/ProcessFlowchartStructureQuery';
import {ProcessTaskGroupFlowchartDataQuery} from './__generated/ProcessTaskGroupFlowchartDataQuery';
import LiveUpdatingTaskGroupFlowchartWrapper from './LiveUpdatingTaskGroupFlowchartWrapper';
import ProcessStaticFlowchartViewer from './ProcessStaticFlowchartViewer';
import buildStaticFlowchartStructureFromGql from './util/buildStaticFlowchartStructureFromGql';
import {mergeTaskGroupLineageIntoFlowchart} from './util/mergeTaskGroupLineageIntoFlowchart';

type FlowchartGql = ExtractByTypename<
  ProcessFlowchartStructureQuery['processFlowcharts'][number],
  'ProcessFlowchartNode'
>;

type ProcessFlowchartStatusLifecycle = ExtractByTypename<
  ExtractByTypename<ProcessFlowchartProcessQuery['process'], 'ProcessNode'>['flowchartRootProcess'],
  'ProcessNode'
>['processTaskSchema']['statusLifecycle'];

type ProcessFlowchartProcessOrInaccessible = ProcessTaskGroupFlowchartDataQuery['process'];
type ProcessFlowchartProcess = ExtractByTypename<ProcessFlowchartProcessOrInaccessible, 'ProcessNode'>;

// This query retrieves essential data for the process being viewed. It redirects to the root process: i.e.,
// whether or not this process is the root of a flowchart, we find the root, then get the essential details
// about that process' tasks. Both the task and flowchart ids are used for follow-up queries (below).
export const PROCESS_FLOWCHART_PROCESS_QUERY = gql`
  query ProcessFlowchartProcessQuery($processId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    process(id: $processId) {
      ... on ProcessNode {
        id
        flowchartRootProcess {
          ... on ProcessNode {
            id
            name
            tasks {
              id
              executionFlowchart {
                id
                numNodes
              }
            }
            flowchartConfigVersion {
              id
              isFlowchartModifiableAfterCreation
            }
            processTaskSchema {
              id
              name
              outputSchema {
                id
              }
              clientOrigin
              statusLifecycle {
                ... on ProcessTaskStatusLifecycleNode {
                  ...ProcessTaskStatusLifecycleGraphQL
                }
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function TaskGroupFlowchartQueryingViewer(props: {
  processId: string;
  onSetProcess: (processId: string) => void;
}): JSX.Element {
  return (
    <BenchlingQuery<ProcessFlowchartProcessQuery, ProcessFlowchartProcessQueryVariables>
      query={PROCESS_FLOWCHART_PROCESS_QUERY}
      variables={{processId: props.processId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
      renderCachedDataWhenRefetching={true}
    >
      {(flowchartProcessData: ProcessFlowchartProcessQuery, flowchartProcessQueryResult) => {
        if (
          isInaccessible(flowchartProcessData.process) ||
          flowchartProcessData.process.flowchartRootProcess == null ||
          isInaccessible(flowchartProcessData.process.flowchartRootProcess)
        ) {
          return <NoFlowchartViewer message="The flowchart is not accessible" />;
        } else {
          const rootProcess = flowchartProcessData.process.flowchartRootProcess;
          // select the first non-null flowchart since all root tasks share the same execution flowchart.
          const flowchart = rootProcess.tasks
            .map((task) => task.executionFlowchart)
            .find((flowchart) => flowchart != null);

          if (flowchart == null) {
            return;
          }

          if (flowchart.numNodes > BenchlingConfig.WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI) {
            return <NoFlowchartViewer message="The flowchart is too large to display" />;
          }
          return (
            <LiveUpdatingTaskGroupFlowchartWrapper
              parentId={rootProcess.id}
              refetch={flowchartProcessQueryResult.refetch}
              flowchartIds={[flowchart.id]}
              processId={rootProcess.id}
            >
              {(flowcharts, flowchartData, forceRefetch) => {
                if (flowcharts.length === 0 || isInaccessible(flowchartData.process)) {
                  return <NoFlowchartViewer message="The flowchart is not accessible" />;
                }

                const newFlowchart = buildStaticFlowchartStructureFromGql({
                  flowchartStructure: flowcharts[0],
                  schema: rootProcess.processTaskSchema,
                  nodeAnalytics: {
                    rootProcessId: rootProcess.id,
                  },
                  metadata: {
                    title: rootProcess.name,
                  },
                });

                const finalFlowchart = mergeTaskGroupLineageIntoFlowchart({
                  flowchart: newFlowchart,
                  rootProcess: flowchartData.process,
                  clientOrigin: rootProcess.processTaskSchema.clientOrigin,
                  processBeingViewed: props.processId,
                  shouldLinkProcess: true,
                  onSetProcess: props.onSetProcess,
                });

                const editFlowchartProps = rootProcess.flowchartConfigVersion
                  ?.isFlowchartModifiableAfterCreation
                  ? {
                      allowFlowchartModifications: true as const,
                      flowchartProcessId: rootProcess.id,
                      rootProcessTaskSchemaId: rootProcess.processTaskSchema.id,
                      sourceType: 'workspace_tab',
                      editDisabledMessage: getEditDisabledMessageFromProcesses(
                        flowcharts[0],
                        flowchartData.process,
                        rootProcess.processTaskSchema.statusLifecycle
                      ),
                      onFlowchartModified: () => {
                        flowchartProcessQueryResult.refetch();
                        forceRefetch();
                      },
                    }
                  : {};

                return (
                  <ProcessStaticFlowchartViewer
                    flowchart={finalFlowchart}
                    actionOrigin={ActionOrigin.WORKFLOWS_TASK_GROUP_PAGE}
                    isFlowchartMappingInProgress={
                      flowchartData.process.isMappingInProgressForFlowchartRootedAtProcess
                    }
                    {...editFlowchartProps}
                  />
                );
              }}
            </LiveUpdatingTaskGroupFlowchartWrapper>
          );
        }
      }}
    </BenchlingQuery>
  );
}

function getEditDisabledMessageFromProcesses(
  flowchartGql: FlowchartGql,
  flowchartRootProcess: ProcessFlowchartProcess,
  statusLifecycle: ProcessFlowchartStatusLifecycle
): string | null {
  if (flowchartGql.nodeConfigs.some((node) => isRouterNodeDetailsGQL(node.nodeDetails))) {
    return 'You cannot edit a flowchart with a router';
  }
  if (flowchartRootProcess.childProcesses.some((process) => isInaccessible(process))) {
    return 'You do not have sufficient permissions to update the flowchart';
  } else if (
    flowchartRootProcess.taskStatusCounts.some((taskStatusCount) =>
      isTerminalStatus(taskStatusCount.status.id, statusLifecycle)
    )
  ) {
    return 'You cannot edit a flowchart with tasks in a terminal status';
  }

  return null;
}

export default TaskGroupFlowchartQueryingViewer;
